/**
 * Per-field **JS toggle** — the Appsmith pattern (ADR-0044 U4).
 *
 * Every scalar page-builder field is set two ways, flipped by a small `JS`
 * badge next to its label:
 *
 *   - **structured** (JS off) — the clean typed control for the field
 *     (a Switch / number input / dropdown / text input), stored as a literal.
 *   - **JS** (JS on) — a `{{ }}` JavaScript binding edited inline (Monaco with
 *     the page's bindings + a live `= value` preview), stored as a string.
 *
 * The mode is DERIVED from the value (`isExpressionValue` — a value is a binding
 * iff it's a string carrying `{{`), so it survives a reload and stays in lockstep
 * with the runtime (the renderer resolves the binding scalar-side via the same
 * rule). This is Tray's existing `ExpressionField` Fixed|ƒx control generalised
 * from string-only to ALL types — one consistent inspector, the owner's "fatta
 * bene" ask: «se è selezionato JS vado di codice, altrimenti piccoli editor
 * ordinati e puliti».
 *
 * The runtime half (so this is never a dead toggle) lives in
 * `PageRenderer.resolveScalarBindings`.
 */
import * as React from "react";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { isExpressionValue } from "@/components/workflows/ExpressionField";
import { cn } from "@/lib/cn";
import type { ExprPreview } from "@/lib/pagePreview";

/** The little `JS` badge — active = the field is a `{{ }}` binding. */
export function JsBadge({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label="JS"
      title={active ? "Espressione JS — torna al controllo" : "Usa un'espressione JS {{ }}"}
      onClick={onToggle}
      className={cn(
        "shrink-0 rounded border px-1 py-px font-mono text-[9px] font-semibold leading-tight transition-colors",
        active
          ? "border-accent bg-accent text-accent-foreground"
          : "border-border/60 text-muted-foreground/70 hover:border-border hover:text-foreground",
      )}
    >
      JS
    </button>
  );
}

/** Wrap the current literal as a single JS expression so its value + type are
 *  preserved when entering JS mode (mirrors `ExpressionField.toggle`). An empty
 *  value seeds a `{{  }}` scaffold (a cursor target that persists as a binding). */
function seedExpression(value: unknown): string {
  if (value === undefined || value === null || value === "") return "{{  }}";
  if (typeof value === "string") return `{{ ${JSON.stringify(value)} }}`;
  // boolean / number → their JS literal form (`true`, `10`).
  return `{{ ${String(value)} }}`;
}

export interface BindableFieldProps {
  label: string;
  hint?: string;
  value: unknown;
  /** The structured control (already wired to value/onChange). Rendered only in
   *  the JS-OFF state — in JS mode the `{{ }}` editor replaces it. */
  children: React.ReactNode;
  /** Where the structured control sits: `row` (right of the label, compact —
   *  switch / number / dropdown) or `stack` (full width under the label — text). */
  layout?: "row" | "stack";
  /** Value to restore when leaving JS mode (the type's neutral literal). */
  fallback: unknown;
  /** A taller editor for prose-like string fields. */
  multiline?: boolean;
  jsAmbientTypes?: string;
  previewExpr?: (template: string) => ExprPreview | null;
  registerInsert?: (insert: ((text: string) => void) | null) => void;
  onChange: (value: unknown) => void;
}

export function BindableField({
  label,
  hint,
  value,
  children,
  layout = "row",
  fallback,
  multiline = false,
  jsAmbientTypes,
  previewExpr,
  registerInsert,
  onChange,
}: BindableFieldProps) {
  const expr = isExpressionValue(value);

  const toggle = () => {
    if (expr) onChange(fallback);
    else onChange(seedExpression(value));
  };

  // Insert-at-cursor wiring (the Data panel drops a `{{ }}` binding at the
  // caret) — register on focus, clear on unmount. Mirrors `ExpressionField`.
  type MonacoEditor = Parameters<
    NonNullable<React.ComponentProps<typeof CodeEditor>["onEditorMount"]>
  >[0];
  React.useEffect(() => () => registerInsert?.(null), [registerInsert]);
  const onEditorMount = React.useCallback(
    (editor: MonacoEditor) => {
      if (!registerInsert) return;
      const insert = (text: string) => {
        const sel = editor.getSelection();
        if (sel) editor.executeEdits("insert-binding", [{ range: sel, text, forceMoveMarkers: true }]);
        editor.focus();
      };
      editor.onDidFocusEditorText(() => registerInsert(insert));
    },
    [registerInsert],
  );

  const stacked = expr || layout === "stack";

  return (
    <div className="flex flex-col gap-1.5 border-b border-border/50 py-2 last:border-0">
      <div className="flex items-center justify-between gap-3">
        <span
          className={cn(
            "flex items-center gap-1.5",
            stacked
              ? "text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              : "text-xs text-muted-foreground",
          )}
        >
          {label}
          <JsBadge active={expr} onToggle={toggle} />
        </span>
        {!expr && layout === "row" && <div className="shrink-0">{children}</div>}
      </div>

      {!expr && layout === "stack" && children}

      {expr && (
        <>
          <div className={cn("w-full", multiline ? "h-40" : "h-24")}>
            <CodeEditor
              value={typeof value === "string" ? value : ""}
              onChange={onChange}
              language="javascript"
              height="100%"
              inline
              jsAmbientTypes={jsAmbientTypes}
              onEditorMount={onEditorMount}
            />
          </div>
          <ExprPreviewLine previewExpr={previewExpr} value={typeof value === "string" ? value : ""} />
        </>
      )}

      {hint && !expr && (
        <p className="text-[11px] leading-snug text-muted-foreground/80">{hint}</p>
      )}
    </div>
  );
}

/** Live `= value` (success tone) or the evaluation error under the JS editor.
 *  Renders nothing without an evaluator or with nothing to preview. */
function ExprPreviewLine({
  previewExpr,
  value,
}: {
  previewExpr?: (template: string) => ExprPreview | null;
  value: string;
}) {
  if (!previewExpr) return null;
  const preview = previewExpr(value);
  if (!preview) return null;
  return preview.ok ? (
    <p
      data-testid="expr-preview"
      className="truncate font-mono text-[11px] text-muted-foreground"
      title={preview.display}
    >
      <span className="text-success">= </span>
      <span>{preview.display}</span>
    </p>
  ) : (
    <p
      data-testid="expr-preview-error"
      className="truncate text-[11px] text-danger/80"
      title={preview.error}
    >
      {preview.error}
    </p>
  );
}
